import { useEffect, useState } from "react";
import { DynamicFieldInput } from "@/components/DynamicFieldInput";
import { applyCountrySelection, mergedScopedValues, scopedMapsForValues, type ScopedDynamicValueMaps } from "@/models/athleteUniversalFields";
import { fetchCountryResources, type CountryResource } from "@/models/countryResource";
import type { LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import type { LeagueAthleteFieldDefinition } from "@/models/fieldDefinition";

// Edits the three privacy-scoped value maps of a competition instance (or
// athlete/org-admin value doc). Values route to the map matching each
// field's privacyScope, mirroring LeagueDynamicFieldValueRouting on iOS.

export type ScopedValueMaps = ScopedDynamicValueMaps;

export function mergedValues(maps: ScopedValueMaps): Record<string, LeagueDynamicFieldValue> {
  return mergedScopedValues(maps);
}

export function settingValue(
  maps: ScopedValueMaps,
  field: LeagueAthleteFieldDefinition,
  value: LeagueDynamicFieldValue | undefined,
): ScopedValueMaps {
  const scopeKey =
    field.privacyScope === "public" ? "publicValues" : field.privacyScope === "organization" ? "organizationValues" : "leagueValues";
  const next: ScopedValueMaps = {
    publicValues: { ...maps.publicValues },
    organizationValues: { ...maps.organizationValues },
    leagueValues: { ...maps.leagueValues },
  };
  // A field lives in exactly one scope; clear stale copies if the scope changed.
  delete next.publicValues[field.id];
  delete next.organizationValues[field.id];
  delete next.leagueValues[field.id];
  if (value !== undefined) {
    next[scopeKey][field.id] = value;
  }
  return next;
}

export function DynamicValuesEditor({
  fields,
  maps,
  onChange,
  fieldErrors = {},
}: {
  fields: LeagueAthleteFieldDefinition[];
  maps: ScopedValueMaps;
  onChange: (maps: ScopedValueMaps) => void;
  fieldErrors?: Record<string, string>;
}) {
  const values = mergedValues(maps);
  const [countries, setCountries] = useState<CountryResource[]>([]);
  const [countryLoadError, setCountryLoadError] = useState<string | null>(null);
  const needsCountries = fields.some((field) => field.universalAthleteRole === "country-name");

  useEffect(() => {
    if (!needsCountries || countries.length > 0) return;
    let isCancelled = false;
    void fetchCountryResources()
      .then((loaded) => {
        if (!isCancelled) {
          setCountries(loaded);
          setCountryLoadError(null);
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) setCountryLoadError(error instanceof Error ? error.message : "Country resources could not be loaded.");
      });
    return () => {
      isCancelled = true;
    };
  }, [countries.length, needsCountries]);

  const selectCountry = (country: CountryResource) => {
    const nextValues = applyCountrySelection(country, values, fields);
    onChange(scopedMapsForValues(nextValues, fields, maps));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {fields.map((field) => (
        <DynamicFieldInput
          key={field.id}
          field={field}
          value={values[field.id]}
          countries={field.universalAthleteRole === "country-name" ? countries : []}
          countryLoadError={field.universalAthleteRole === "country-name" ? countryLoadError : null}
          onCountrySelect={selectCountry}
          onChange={(value) => onChange(settingValue(maps, field, value))}
          errorMessage={fieldErrors[field.id]}
        />
      ))}
      {fields.length === 0 && <p className="home-hint">The template defines no fields.</p>}
    </div>
  );
}
